import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
  ApiHolder,
} from '@backstage/core-plugin-api';
import type {
  ToolCall,
  File,
  Choice,
  ConversationMetadata,
  Document,
  Action,
  Plot,
  Evaluation,
  UserFavorite,
} from '../types';
import type { ResponseEmbedding } from '@internal/plugin-rag-ai';
import { RagAssistantAdapter, RAG_ASSISTANT_ID } from './RagAssistantAdapter';
import {
  BackstageAgentAdapter,
  BACKSTAGE_AGENT_ASSISTANT_ID,
} from './BackstageAgentAdapter';
import {
  getLocalAssistantInfo,
  isLocalAssistantId,
  isLocalConversationId,
  isRagConversationId,
  isBackstageAgentConversationId,
  toAssistantInfo,
} from './LocalAssistants';
import { MessageRole } from './constants';

export interface ConversationAssistant {
  id: string;
  name: string;
  logoUrl?: string;
  model?: string;
  favoritedAt?: string;
  topic?: string;
  instructions?: string;
  description?: string;
}

export interface ChatMessage {
  role:
    | typeof MessageRole.SYSTEM
    | typeof MessageRole.USER
    | typeof MessageRole.ASSISTANT;
  content: string;
}

export interface ChatAgentMessage {
  role:
    | typeof MessageRole.USER
    | typeof MessageRole.ASSISTANT
    | typeof MessageRole.SYSTEM
    | typeof MessageRole.TOOL;
  content: string;
  name?: string;
  tool_call_id?: string;
  tool_calls?: ToolCall[];
}

export interface ChatAgentRequest {
  messages: ChatAgentMessage[];
  assistant_id: string;
  user_id?: string;
  conversation_id?: string;
  metadata?: ConversationMetadata;
}

export interface ChatAgentResponse {
  id: string;
  parent_id: string | null;
  object: string | null;
  created: number;
  model: string;
  system_fingerprint: string | null;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  choices: Array<{
    message: {
      role: string;
      content: string;
      tool_calls?: ToolCall[];
    };
    finish_reason?: string;
    index?: number;
  }>;
  prompt_annotations: Record<string, unknown> | null;
  cost: number;
  conversation_id: string;
  metadata: Array<{
    cost: {
      total_cost: number;
    };
    choices: Choice[];
  }>;
}

export interface AssistantInfo {
  id: string;
  name: string;
  description: string;
  instructions: string;
  temperature: number;
  model: {
    model: string;
    name: string;
    description: string;
  };
  topic: string;
  tools: ToolCall[];
  files: File[];
  starter_messages: Array<{ message: string }>;
  total_conversations: number;
  total_stars: number;
  total_unique_users: number;
  logo_url: string;
  creation_date: string;
  modification_date: string;
}

export interface AgentToolCallTrace {
  step: number;
  llmTurn: number;
  toolName: string;
  args: Record<string, unknown>;
  isError: boolean;
  durationMs: number;
}

export interface MgaAssistantsResponse {
  all: AssistantInfo[];
}

export interface ConversationReference {
  id: string;
  title: string;
  updatedAt: string;
  assistantId: string;
  messageCount?: number;
}

export interface ConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
  lastMessage?: string;
  messageCount: number;
  assistant: {
    id: string;
    name: string;
    logoUrl?: string;
    model?: string;
  };
  totalCost?: number;
}

// Default types - only include fields actually used by frontend
export interface Message {
  id: string;
  role: typeof MessageRole.USER | typeof MessageRole.ASSISTANT;
  content: string;
  cost: number;
  creation_datetime: string;
  feedback_sentiment?: string | null;
  meta_data?: ConversationMetadata;
  conversation_id?: string;
  parent_id?: string | null;
  embeddings?: ResponseEmbedding[];
  agentToolCalls?: AgentToolCallTrace[];
  agentProgress?: string[];
  metadata?: {
    documents: Document[];
    actions: Action[];
    file_ids: string[] | null;
    evaluation: Evaluation | null;
    plots: Plot[];
  };
}

export interface Assistant {
  id: string;
  name: string;
  description: string;
  logo_url: string;
  model: {
    model: string;
    name: string;
  };
  topic?: string;
  instructions?: string;
  total_conversations?: number;
  total_unique_users?: number;
  modification_date?: string;
  favorited?: boolean;
  tools: ToolCall[];
  files: File[];
}

export interface Conversation {
  id: string;
  name: string;
  modification_date: string;
  messages: Message[];
  assistant: Assistant;
  total_cost: number;
  creation_date?: string;
  account_id?: number;
  agree_to_share?: boolean;
  model_id?: string;
  instructions?: string;
  temperature?: number;
  description?: string | null;
  disabled?: boolean;
  is_pinned?: boolean;
  hidden?: boolean;
  azure_thread_id?: string | null;
  memory_length?: number | null;
  follow_up_questions?: string | null;
  deleted_at?: string | null;
  tools: ToolCall[];
  files: File[];
}

export interface ChatCompletionRequest {
  messages: ChatMessage[];
  model?: string;
  conversationId?: string;
  assistantId?: string;
}

export interface ChatCompletionResponse {
  content: string;
  usage?: {
    totalTokens: number;
    cost?: number;
  };
  model: string;
  conversationId?: string;
  internalConversationId?: string;
}

export interface MgaHealthResponse {
  status: 'ok' | 'error';
}

export interface MgaModelsResponse {
  models: string[];
}

export interface MgaUsageStats {
  totalConversations: number;
  totalMessages: number;
  totalTokens: number;
  totalCost: number;
  averageTokensPerMessage: number;
  mostUsedModel: string;
  thisMonthUsage: {
    conversations: number;
    messages: number;
    tokens: number;
    cost: number;
  };
  lastUpdated: string;
}

export interface MgaApi {
  // core chat
  chatCompletion(
    request: ChatCompletionRequest,
  ): Promise<ChatCompletionResponse>;
  chatAgent(request: ChatAgentRequest): Promise<ChatAgentResponse>;

  // metadata
  healthCheck(): Promise<MgaHealthResponse>;
  // getModels(): Promise<MgaModelsResponse>;
  // getUserUsageStats(): Promise<MgaUsageStats>;
  getAssistants(
    limit?: number,
    offset?: number,
    keyword?: string,
  ): Promise<MgaAssistantsResponse>;
  getAssistantById(assistantId: string): Promise<AssistantInfo>;

  // favorites
  getFavoriteAssistants(): Promise<ConversationAssistant[]>;
  favoriteAssistant(assistant: ConversationAssistant): Promise<void>;
  unfavoriteAssistant(assistantId: string): Promise<void>;
  isAssistantFavorited(assistantId: string): Promise<boolean>;

  // conversations
  listConversations(): Promise<ConversationReference[]>;
  listConversationSummaries(): Promise<ConversationSummary[]>;
  createConversation(request: {
    title: string;
    assistantId: string;
  }): Promise<Conversation>;
  getConversationById(id: string): Promise<Conversation>;
  getConversationsByIds(ids: string[]): Promise<Conversation[]>;
  updateConversationTitle(id: string, title: string): Promise<void>;
  deleteConversation(id: string): Promise<void>;

  /** Wires local adapters that need ApiHolder (RAG, Backstage Agent). */
  setApiHolder?(apiHolder: ApiHolder): void;

  /** Shared in-process adapters for local assistants (same instances as conversation APIs). */
  getLocalAssistantAdapter?(
    assistantId: string,
  ): RagAssistantAdapter | BackstageAgentAdapter | undefined;
}

export const mgaApiRef = createApiRef<MgaApi>({
  id: 'plugin.mga.service',
});

export class MgaApiClient implements MgaApi {
  private ragAdapter: RagAssistantAdapter | null = null;
  private backstageAgentAdapter: BackstageAgentAdapter | null = null;

  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  setApiHolder(apiHolder: ApiHolder) {
    this.ragAdapter = new RagAssistantAdapter(apiHolder);
    this.backstageAgentAdapter = new BackstageAgentAdapter(apiHolder);
  }

  getLocalAssistantAdapter(
    assistantId: string,
  ): RagAssistantAdapter | BackstageAgentAdapter | undefined {
    if (!this.ragAdapter || !this.backstageAgentAdapter) {
      return undefined;
    }
    if (assistantId === RAG_ASSISTANT_ID) {
      return this.ragAdapter;
    }
    if (assistantId === BACKSTAGE_AGENT_ASSISTANT_ID) {
      return this.backstageAgentAdapter;
    }
    return undefined;
  }

  private async getBaseUrl() {
    return this.discoveryApi.getBaseUrl('mga');
  }

  private getLocalConversationById(id: string): Conversation | null {
    if (isRagConversationId(id) && this.ragAdapter) {
      const conversation = this.ragAdapter.getConversation(id);
      return conversation
        ? this.ragAdapter.convertToMgaConversation(conversation)
        : null;
    }

    if (isBackstageAgentConversationId(id) && this.backstageAgentAdapter) {
      const conversation = this.backstageAgentAdapter.getConversation(id);
      return conversation
        ? this.backstageAgentAdapter.convertToMgaConversation(conversation)
        : null;
    }

    return null;
  }

  async chatCompletion(
    request: ChatCompletionRequest,
  ): Promise<ChatCompletionResponse> {
    const url = `${await this.getBaseUrl()}/chat/completions`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async createChatCompletion(
    request: ChatCompletionRequest,
  ): Promise<ChatCompletionResponse> {
    const url = `${await this.getBaseUrl()}/chat/completions`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async chatAgent(request: ChatAgentRequest): Promise<ChatAgentResponse> {
    const url = `${await this.getBaseUrl()}/chat/agent`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  // metadata
  async healthCheck(): Promise<MgaHealthResponse> {
    const url = `${await this.getBaseUrl()}/health`;
    const response = await this.fetchApi.fetch(url, { method: 'GET' });
    if (!response.ok)
      throw new Error(
        `MGA API health check failed: ${response.status} ${response.statusText}`,
      );
    return response.json();
  }

  async getAssistants(
    limit?: number,
    offset?: number,
    keyword?: string,
  ): Promise<MgaAssistantsResponse> {
    const baseUrl = await this.getBaseUrl();
    const params = new URLSearchParams();

    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    if (keyword) params.append('keyword', keyword);

    const url = `${baseUrl}/assistants${
      params.toString() ? `?${params.toString()}` : ''
    }`;
    const response = await this.fetchApi.fetch(url, { method: 'GET' });
    if (!response.ok)
      throw new Error(
        `MGA API assistants request failed: ${response.status} ${response.statusText}`,
      );
    return response.json();
  }

  async getAssistantById(assistantId: string): Promise<AssistantInfo> {
    const localAssistant = getLocalAssistantInfo(assistantId);
    if (localAssistant) {
      return toAssistantInfo(localAssistant);
    }

    // Regular MGA API call for other assistants
    const baseUrl = await this.getBaseUrl();
    const url = `${baseUrl}/assistants/${assistantId}`;
    const response = await this.fetchApi.fetch(url, { method: 'GET' });
    if (!response.ok)
      throw new Error(
        `MGA API assistant by ID request failed: ${response.status} ${response.statusText}`,
      );
    return response.json();
  }

  async getFavoriteAssistants(): Promise<ConversationAssistant[]> {
    const url = `${await this.getBaseUrl()}/favorites?type=assistant`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw new Error(await response.text());
    const favorites: UserFavorite[] = await response.json();
    return favorites.map(fav => ({
      id: fav.item_id,
      name: fav.name,
      favoritedAt: fav.favorited_at,
      logoUrl: fav.logo_url || '',
      model: fav.model || '',
      description: fav.description || '',
    }));
  }

  async favoriteAssistant(assistant: ConversationAssistant): Promise<void> {
    const url = `${await this.getBaseUrl()}/favorites`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        itemId: assistant.id,
        itemType: 'assistant',
        name: assistant.name,
        description: assistant.description,
        logoUrl: assistant.logoUrl,
        model: assistant.model,
      }),
    });
    if (!response.ok) throw new Error(await response.text());
  }

  async unfavoriteAssistant(assistantId: string): Promise<void> {
    const url = `${await this.getBaseUrl()}/favorites/assistant/${assistantId}`;
    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) throw new Error(await response.text());
  }

  async isAssistantFavorited(assistantId: string): Promise<boolean> {
    const url = `${await this.getBaseUrl()}/favorites/assistant/${assistantId}/check`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) return false;
    const { isFavorited } = await response.json();
    return isFavorited;
  }

  async listConversations(): Promise<ConversationReference[]> {
    const url = `${await this.getBaseUrl()}/conversations`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async createConversation(request: {
    title: string;
    assistantId: string;
  }): Promise<Conversation> {
    const url = `${await this.getBaseUrl()}/conversations`;
    const response = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async getConversationById(id: string): Promise<Conversation> {
    const localConversation = this.getLocalConversationById(id);
    if (localConversation) {
      return localConversation;
    }

    if (isLocalConversationId(id)) {
      throw new Error(`Conversation not found: ${id}`);
    }

    const url = `${await this.getBaseUrl()}/conversations/${id}`;
    const response = await this.fetchApi.fetch(url);
    if (!response.ok) throw new Error(await response.text());
    return response.json();
  }

  async getConversationsByIds(ids: string[]): Promise<Conversation[]> {
    const conversationsById = new Map<string, Conversation>();
    const remoteIds: string[] = [];

    for (const id of ids) {
      const localConversation = this.getLocalConversationById(id);
      if (localConversation) {
        conversationsById.set(id, localConversation);
      } else if (!isLocalConversationId(id)) {
        remoteIds.push(id);
      }
    }

    if (remoteIds.length > 0) {
      const url = `${await this.getBaseUrl()}/conversations/batch`;
      const response = await this.fetchApi.fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: remoteIds }),
      });
      if (!response.ok) throw new Error(await response.text());
      const remoteConversations: Conversation[] = await response.json();
      for (const conversation of remoteConversations) {
        conversationsById.set(conversation.id, conversation);
      }
    }

    return ids
      .map(id => conversationsById.get(id))
      .filter((conversation): conversation is Conversation => !!conversation);
  }

  async updateConversationTitle(id: string, title: string): Promise<void> {
    if (isRagConversationId(id) && this.ragAdapter) {
      this.ragAdapter.updateConversationTitle(id, title);
      return;
    }
    if (isBackstageAgentConversationId(id) && this.backstageAgentAdapter) {
      this.backstageAgentAdapter.updateConversationTitle(id, title);
      return;
    }

    const url = `${await this.getBaseUrl()}/conversations/${id}`;
    const response = await this.fetchApi.fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    if (!response.ok) throw new Error(await response.text());
  }

  async deleteConversation(id: string): Promise<void> {
    if (isRagConversationId(id) && this.ragAdapter) {
      this.ragAdapter.deleteConversation(id);
      return;
    }
    if (isBackstageAgentConversationId(id) && this.backstageAgentAdapter) {
      this.backstageAgentAdapter.deleteConversation(id);
      return;
    }

    const url = `${await this.getBaseUrl()}/conversations/${id}`;
    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) throw new Error(await response.text());
  }

  async listConversationSummaries(): Promise<ConversationSummary[]> {
    let mgaConversations: ConversationSummary[] = [];
    let mgaError: Error | undefined;

    try {
      const url = `${await this.getBaseUrl()}/conversations/summaries`;
      const response = await this.fetchApi.fetch(url);
      if (response.ok) {
        mgaConversations = await response.json();
      } else {
        mgaError = new Error(await response.text());
      }
    } catch (error) {
      mgaError =
        error instanceof Error ? error : new Error('Failed to load conversations');
    }

    const localSummaries: ConversationSummary[] = [];

    if (this.ragAdapter) {
      localSummaries.push(
        ...this.ragAdapter.getAllConversations().map(ragConvo => ({
          id: ragConvo.id,
          title: ragConvo.title,
          updatedAt: ragConvo.timestamp,
          lastMessage: ragConvo.messages[
            ragConvo.messages.length - 1
          ]?.content?.substring(0, 100),
          messageCount: ragConvo.messages.length,
          assistant: {
            id: this.ragAdapter!.assistantInfo.id,
            name: this.ragAdapter!.assistantInfo.name,
            logoUrl: this.ragAdapter!.assistantInfo.logoUrl,
            model: this.ragAdapter!.assistantInfo.model,
          },
          totalCost: 0,
        })),
      );
    }

    if (this.backstageAgentAdapter) {
      localSummaries.push(
        ...this.backstageAgentAdapter.getAllConversations().map(agentConvo => ({
          id: agentConvo.id,
          title: agentConvo.title,
          updatedAt: agentConvo.timestamp,
          lastMessage: agentConvo.messages[
            agentConvo.messages.length - 1
          ]?.content?.substring(0, 100),
          messageCount: agentConvo.messages.length,
          assistant: {
            id: this.backstageAgentAdapter!.assistantInfo.id,
            name: this.backstageAgentAdapter!.assistantInfo.name,
            logoUrl: this.backstageAgentAdapter!.assistantInfo.logoUrl,
            model: this.backstageAgentAdapter!.assistantInfo.model,
          },
          totalCost: 0,
        })),
      );
    }

    if (localSummaries.length > 0) {
      const localIds = new Set(localSummaries.map(summary => summary.id));
      const filteredMgaConversations = mgaConversations.filter(
        mgaConvo =>
          !localIds.has(mgaConvo.id) &&
          !isLocalAssistantId(mgaConvo.assistant.id),
      );

      // Merge and sort by update time
      const allConversations = [...filteredMgaConversations, ...localSummaries];
      allConversations.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );

      return allConversations;
    }

    if (mgaError) {
      throw mgaError;
    }

    return mgaConversations;
  }
}
